import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { keyframes } from '@mui/system'

export function promiseAllStepN(n, list) {
  let tail = list.splice(n)
  let head = list
  let resolved = []
  let processed = 0
  return new Promise((resolve, reject) => {
    head.forEach((x) => {
      let res = x()
      resolved.push(res)
      res
        .then((y) => {
          runNext()
          return y
        })
        .catch((e) => {
          runNext()
        })
    })
    function runNext() {
      if (processed === tail.length) {
        resolve(Promise.all(resolved))
      } else {
        resolved.push(
          tail[processed]().then((x) => {
            runNext()
            return x
          })
        )
        processed++
      }
    }
  })
}

export const setPromiseConcurrent = () => {
  Promise.allConcurrent = (n) => (list) => promiseAllStepN(n, list)
}

export const decodeHtmlCharCodes = (str) => {
  if (str) {
    return str.toString().replace(/(&#(\d+);)/g, function (charCode) {
      return String.fromCharCode(charCode)
    })
  }
  return ''
}

export const usePrevious = (value) => {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export const month = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
}
export const getFormattedDate = (dbDate) => {
  if (!dbDate) return 'N/A'
  const date = new Date(dbDate)
  var year = date.getFullYear()

  var month = (1 + date.getMonth()).toString()
  month = month.length > 1 ? month : '0' + month

  var day = date.getDate().toString()
  day = day.length > 1 ? day : '0' + day

  return month + '/' + day + '/' + year
}

export const getRedirectUrl = () => {
  return localStorage.getItem('redirectUrl')
}

// Notifications
export function formatCreatedAt(iso) {
  const d = new Date(iso)
  const now = new Date()
  if (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  ) {
    const diff = now - d
    const mins = Math.floor(diff / 1000 / 60)
    if (mins < 1) return `${Math.floor(diff / 1000)} secs ago`
    if (mins < 60) return `${mins} mins ago`
    const hrs = Math.floor(mins / 60)
    return `${hrs} hrs ago`
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// filter options
export const READ_STATUS_OPTIONS = [
  { key: 'all', label: 'All notifications' },
  { key: 'unread', label: 'Unread' },
]

export const FILTER_OPTIONS = [
  { key: '', label: 'All types' },
  { key: 'TASK', label: 'Task' },
  { key: 'PROJECT', label: 'Project' },
  { key: 'REQUEST', label: 'Request & Approval' },
  { key: 'COMMENT', label: 'Comment' },
]

export const TYPE_ICON_COLORS = {
  task: '#B85300', // Orange/brown for task notifications
  project: '#1B7E7E', // Teal for project notifications
  input: '#1B7E7E', // Teal for input files
  file: '#1B7E7E', // Teal for files
  comment: '#1B7E7E', // Teal for comment notifications
  default: '#9E9E9E', // Gray for other notifications
}

// Lighter versions for read notifications (50% opacity)
export const TYPE_ICON_COLORS_READ = {
  task: '#D4A980', // Lighter version of #B85300
  project: '#8DBFBF', // Lighter version of #1B7E7E
  input: '#8DBFBF', // Lighter version of #1B7E7E
  file: '#8DBFBF', // Lighter version of #1B7E7E
  comment: '#8DBFBF', // Lighter version of #1B7E7E
  default: '#CFCFCF', // Lighter gray for other read notifications
}

export const sectionTitleStyle = {
  background: '#F6F7F8',
  fontSize: 13,
  color: '#676B74',
}

export function getTypeColor(type, isRead = false) {
  const normalizedType = type?.toLowerCase()
  const colorMap = isRead ? TYPE_ICON_COLORS_READ : TYPE_ICON_COLORS
  return colorMap[normalizedType] || colorMap.default
}

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.3; }
  50% { transform: scale(1.3); opacity: 1; }
  100% { transform: scale(1); opacity: 0.3; }
`

export function LoadingDots() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            animation: `${pulse} 1s ease-in-out infinite`,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </Box>
  )
}

export const isToday = (dateString) => {
  const today = new Date()
  const date = new Date(dateString)
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

const SEEN_TOAST_KEY = 'seenToastIds'

// Load from localStorage
const initial = JSON.parse(localStorage.getItem(SEEN_TOAST_KEY) || '[]')
export const seenToastIds = new Set(initial)

// Check if a toast has been shown
export const hasSeenToast = (id) => seenToastIds.has(id)

// Mark it as shown
export const markToastSeen = (id) => {
  seenToastIds.add(id)
  localStorage.setItem(SEEN_TOAST_KEY, JSON.stringify([...seenToastIds]))
}

// Helper: returns true if the date is more than 24 hours ago
export function isOlderThan24Hours(dateString) {
  const now = new Date()
  const created = new Date(dateString)
  return now - created > 24 * 60 * 60 * 1000
}
